import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Bundle, BundleEntry, Composition, Patient, Encounter, Observation, ServiceRequest, DiagnosticReport, Condition, MedicationRequest, AllergyIntolerance, HumanName, Immunization, Practitioner, PractitionerRole, Organization, Resource } from 'fhir/r4';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChipsDirective } from '../chips.directive';
import { commonImports } from '../shared/table-interface';
import { UtilityService } from '../shared/utility.service';
import { DiagnosticReportDetailsComponent } from '../diagnostic-report/diagnostic-report-details/diagnostic-report-details.component';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { PatSympService } from '../pat-symp/pat-symp.service';

interface DocumentMetadata {
  title: string;
  status: string;
  date?: string;
  authors: string[];
  facility?: string;
}

interface ReferralDocumentViewModel {
  metadata: DocumentMetadata;
  patient?: Patient;
  encounter?: Encounter;
  composition?: Composition;
  referralNotes: string[];
  allergies: AllergyIntolerance[];
  vitals: Observation[];
  physicalExam: Observation[];
  patientSymptoms: Observation[];
  labRequests: ServiceRequest[];
  otherServiceRequests: ServiceRequest[];
  diagnosticReports: DiagnosticReport[];
  currentMedications: MedicationRequest[];
  pastMedications: MedicationRequest[];
  diagnoses: Condition[];
  pastDiagnoses: Condition[];
  immunizations: Immunization[];
}

@Component({
  selector: 'app-referral-document-viewer',
  standalone: true,
  imports: [
    ...commonImports,
    MatCardModule,
    MatExpansionModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatButtonModule,
    MatTooltipModule,
    DatePipe,
    ChipsDirective,
    EmphasisDirective
  ],
  templateUrl: './referral-document-viewer.component.html',
  styleUrls: ['./referral-document-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReferralDocumentViewerComponent implements OnChanges {
  private readonly utility = inject(UtilityService);
  private readonly dialog = inject(MatDialog);
  private readonly patSympService = inject(PatSympService);

  /** Incoming referral bundle (FHIR Bundle of type document). */
  @Input() referralBundle?: Bundle;
  /** Optional override for the header title. */
  @Input() fallbackTitle = 'Referral Document';

  viewModel?: ReferralDocumentViewModel;

  readonly vitalColumns = ['code', 'value', 'status', 'effective'];
  readonly examColumns = ['name', 'value', 'effective'];
  readonly labColumns = ['status', 'code', 'requested', 'reports'];
  readonly serviceColumns = ['status', 'code', 'requested', 'reports'];
  readonly diagnosisColumns = ['status', 'verification', 'diagnosis', 'recorded'];
  readonly medicationColumns = ['medication', 'dose', 'instructions'];
  readonly immunizationColumns = ['status', 'vaccine', 'occurrence'];

  private resourceIndex = new Map<string, Resource>();
  private serviceRequestReportIndex = new Map<string, DiagnosticReport[]>();

  ngOnChanges(changes: SimpleChanges): void {
    if ('referralBundle' in changes) {
      this.viewModel = this.buildViewModel(this.referralBundle);
    }
  }

  get hasBundle(): boolean {
    return !!this.referralBundle && !!this.viewModel;
  }

  observationDisplayName(obs?: Observation): string {
    return this.utility.chooseFirstStringFromCodeableConcept(obs?.code) || obs?.code?.text || 'Observation';
  }

  observationDisplayValue(obs?: Observation): string {
    if (!obs) return '—';
    if (obs.valueQuantity) {
      const unit = obs.valueQuantity.unit || obs.valueQuantity.code || '';
      return `${obs.valueQuantity.value ?? ''} ${unit}`.trim();
    }
    if (obs.valueString) return obs.valueString;
    if (obs.valueCodeableConcept) {
      return this.utility.chooseFirstStringFromCodeableConcept(obs.valueCodeableConcept) || '—';
    }
    if (obs.component?.length) {
      const parts = obs.component.map(comp => this.observationDisplayValue(comp as Observation)).filter(Boolean);
      if (parts.length) {
        return parts.join(' / ');
      }
    }
    return this.utility.convertAllValueTypesToString(obs as any) || '—';
  }

  symptomCategoryCode(obs?: Observation): 'exam' | 'social-history' | '' {
    const categoryCodes = (obs?.category ?? [])
      .flatMap(category => category.coding ?? [])
      .map(coding => `${coding.code ?? ''}`.toLowerCase());

    if (categoryCodes.includes('exam')) {
      return 'exam';
    }
    if (categoryCodes.includes('social-history')) {
      return 'social-history';
    }
    return '';
  }

  symptomCategoryLabel(obs?: Observation): string {
    const categoryCode = this.symptomCategoryCode(obs);
    return categoryCode === 'exam'
      ? 'Symptom'
      : categoryCode === 'social-history'
        ? 'Social / Risk Factor'
        : 'Item';
  }

  symptomCategoryIcon(obs?: Observation): string {
    const categoryCode = this.symptomCategoryCode(obs);
    return categoryCode === 'exam'
      ? 'healing'
      : categoryCode === 'social-history'
        ? 'people'
        : 'notes';
  }

  symptomIsChiefComplaint(obs?: Observation): boolean {
    return (obs?.category ?? []).some(category =>
      (category.coding ?? []).some(coding => `${coding.code ?? ''}`.toLowerCase() === 'chief-complaint')
    );
  }

  symptomEffectivePeriod(obs?: Observation): { start?: string; end?: string } {
    return {
      start: obs?.effectivePeriod?.start ?? obs?.effectiveDateTime ?? undefined,
      end: obs?.effectivePeriod?.end ?? undefined
    };
  }

  symptomDuration(obs?: Observation): string {
    return this.patSympService.getDurationFromEffectivePeriod(this.symptomEffectivePeriod(obs));
  }

  medicationName(request: MedicationRequest): string {
    if (request.medicationCodeableConcept) {
      return this.utility.chooseFirstStringFromCodeableConcept(request.medicationCodeableConcept) || 'Medication';
    }
    if (request.medicationReference?.display) {
      return request.medicationReference.display;
    }
    return request.medicationReference?.reference || 'Medication';
  }

  medicationDose(request: MedicationRequest): string {
    const doseQuantity = request.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity;
    if (doseQuantity?.value) {
      return `${doseQuantity.value} ${doseQuantity.unit || doseQuantity.code || ''}`.trim();
    }
    return request.dosageInstruction?.[0]?.text || '—';
  }

  medicationInstructions(request: MedicationRequest): string {
    const timing = request.dosageInstruction?.[0]?.timing?.code?.text;
    if (timing) return timing;
    const repeat = request.dosageInstruction?.[0]?.timing?.repeat;
    if (repeat?.frequency && repeat?.periodUnit) {
      return `${repeat.frequency}x per ${repeat.periodUnit}`;
    }
    return '—';
  }

  private buildViewModel(bundle?: Bundle): ReferralDocumentViewModel | undefined {
    if (!bundle) return undefined;

    this.resourceIndex = this.buildResourceIndex(bundle.entry ?? []);

    const composition = this.firstOfType<Composition>('Composition');
    const patient = this.resolveReference<Patient>(composition?.subject?.reference) || this.firstOfType<Patient>('Patient');
    const encounter = this.resolveReference<Encounter>(composition?.encounter?.reference) || this.firstOfType<Encounter>('Encounter');

    const observations = this.resourcesOfType<Observation>('Observation');
    const serviceRequests = this.resourcesOfType<ServiceRequest>('ServiceRequest');
    const diagnosticReports = this.resourcesOfType<DiagnosticReport>('DiagnosticReport');
    const conditions = this.resourcesOfType<Condition>('Condition');
    const medicationRequests = this.resourcesOfType<MedicationRequest>('MedicationRequest');
    const allergies = this.resourcesOfType<AllergyIntolerance>('AllergyIntolerance');
    const immunizations = this.resourcesOfType<Immunization>('Immunization');

    const patientId = patient?.id || this.normalizeReference(composition?.subject?.reference);
    const encounterId = encounter?.id || this.normalizeReference(composition?.encounter?.reference);

    const vitals = this.filterObservationsByCategory(observations, 'vital-signs', patientId, encounterId);
    const physicalExam = this.filterPhysicalExamObservations(observations, patientId, encounterId);
    const patientSymptoms = this
      .filterPatientSymptomObservations(observations, patientId, encounterId)
      .sort((left, right) => {
        const leftChief = this.symptomIsChiefComplaint(left);
        const rightChief = this.symptomIsChiefComplaint(right);
        if (leftChief && !rightChief) {
          return -1;
        }
        if (!leftChief && rightChief) {
          return 1;
        }

        const leftTime = new Date(left.effectivePeriod?.start || left.effectiveDateTime || left.issued || 0).getTime();
        const rightTime = new Date(right.effectivePeriod?.start || right.effectiveDateTime || right.issued || 0).getTime();
        return rightTime - leftTime;
      });

    const scopedServiceRequests = serviceRequests.filter(sr => {
      if (patientId && !this.referenceMatches(sr.subject?.reference, 'Patient', patientId)) {
        return false;
      }
      if (encounterId && sr.encounter?.reference && !this.referenceMatches(sr.encounter.reference, 'Encounter', encounterId)) {
        return false;
      }
      return true;
    });

    const scopedDiagnosticReports = diagnosticReports.filter(report => {
      if (patientId && !this.referenceMatches(report.subject?.reference, 'Patient', patientId)) {
        return false;
      }
      if (encounterId && report.encounter?.reference && !this.referenceMatches(report.encounter.reference, 'Encounter', encounterId)) {
        return false;
      }
      return true;
    });

    const patientMedicationRequests = medicationRequests.filter(request => {
      return !patientId || this.referenceMatches(request.subject?.reference, 'Patient', patientId);
    });

    const currentMedications = patientMedicationRequests.filter(request => {
      return encounterId && request.encounter?.reference && this.referenceMatches(request.encounter.reference, 'Encounter', encounterId);
    });

    const pastMedications = patientMedicationRequests.filter(request => {
      return !currentMedications.includes(request);
    });

    const scopedAllergies = allergies.filter(allergy => {
      if (patientId && !this.referenceMatches(allergy.patient?.reference, 'Patient', patientId)) {
        return false;
      }
      return true;
    });

    const scopedImmunizations = immunizations.filter(immunization => {
      if (patientId && !this.referenceMatches(immunization.patient?.reference, 'Patient', patientId)) {
        return false;
      }
      if (encounterId && immunization.encounter?.reference && !this.referenceMatches(immunization.encounter.reference, 'Encounter', encounterId)) {
        return false;
      }
      return true;
    });

    this.serviceRequestReportIndex = this.buildServiceRequestReportIndex(scopedDiagnosticReports);

    return {
      metadata: this.buildMetadata(composition),
      patient,
      encounter,
      composition,
      referralNotes: this.collectReferralNotes(composition, scopedServiceRequests),
      allergies: scopedAllergies,
      vitals,
      physicalExam,
      patientSymptoms,
      labRequests: scopedServiceRequests.filter(request => this.isLabServiceRequest(request)),
      otherServiceRequests: scopedServiceRequests.filter(request => !this.isLabServiceRequest(request)),
      diagnosticReports: scopedDiagnosticReports,
      currentMedications,
      pastMedications,
      diagnoses: conditions.filter(condition => this.isCurrentDiagnosis(condition, patientId, encounterId)),
      pastDiagnoses: conditions.filter(condition => this.isHistoricalDiagnosis(condition, patientId, encounterId)),
      immunizations: scopedImmunizations
    };
  }

  private collectReferralNotes(composition: Composition | undefined, serviceRequests: ServiceRequest[]): string[] {
    const compositionNotes = (composition?.section ?? [])
      .filter(section => `${section.title ?? ''}`.trim().toLowerCase() === 'referral notes')
      .map(section => this.stripHtml(`${section.text?.div ?? ''}`))
      .map(note => note.trim())
      .filter(Boolean);

    const requestNotes = serviceRequests
      .flatMap(request => request.note ?? [])
      .map(note => `${note?.text ?? ''}`.trim())
      .filter(Boolean);

    return Array.from(new Set([...compositionNotes, ...requestNotes]));
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  relatedReportsForRequest(request: ServiceRequest | undefined): DiagnosticReport[] {
    if (!request?.id) {
      return [];
    }
    const key = `ServiceRequest/${request.id}`;
    return this.serviceRequestReportIndex.get(key) ?? [];
  }

  openDiagnosticReport(report: DiagnosticReport, event?: Event): void {
    event?.stopPropagation();
    if (!report) {
      return;
    }
    this.dialog.open(DiagnosticReportDetailsComponent, {
      maxWidth: '92vw',
      maxHeight: '94vh',
      data: { diagnosticReport: report }
    });
  }

  private buildMetadata(composition?: Composition): DocumentMetadata {
    if (!composition) {
      return { title: this.fallbackTitle, status: 'unknown', authors: [] };
    }
    const authors = (composition.author ?? [])
      .map(authorRef => this.resolveReference<Resource>(authorRef.reference))
      .map(res => this.displayNameForResource(res))
      .filter(Boolean) as string[];

    const facilityRef = composition.custodian?.reference;
    const facility = this.displayNameForResource(this.resolveReference<Resource>(facilityRef));

    return {
      title: composition.title || this.fallbackTitle,
      status: composition.status || 'unknown',
      date: composition.date,
      authors,
      facility
    };
  }

  private buildResourceIndex(entries: BundleEntry[]): Map<string, Resource> {
    const map = new Map<string, Resource>();
    for (const entry of entries) {
      if (!entry.resource) continue;
      const res = entry.resource as Resource;
      if (res.id) {
        map.set(`${res.resourceType}/${res.id}`, res);
      }
      if (entry.fullUrl) {
        map.set(entry.fullUrl, res);
      }
    }
    return map;
  }

  private buildServiceRequestReportIndex(reports: DiagnosticReport[]): Map<string, DiagnosticReport[]> {
    const map = new Map<string, DiagnosticReport[]>();
    for (const report of reports) {
      const basedOn = Array.isArray(report.basedOn) ? report.basedOn : [];
      for (const reference of basedOn) {
        const normalized = this.normalizeReference(reference.reference);
        if (!normalized || !normalized.startsWith('ServiceRequest/')) {
          continue;
        }
        const bucket = map.get(normalized) ?? [];
        bucket.push(report);
        map.set(normalized, bucket);
      }
    }
    return map;
  }

  private resourcesOfType<T extends Resource>(resourceType: string): T[] {
    const list: T[] = [];
    for (const value of this.resourceIndex.values()) {
      if (value.resourceType === resourceType) {
        list.push(value as T);
      }
    }
    return list;
  }

  private firstOfType<T extends Resource>(resourceType: string): T | undefined {
    return this.resourcesOfType<T>(resourceType)[0];
  }

  private resolveReference<T extends Resource>(reference?: string): T | undefined {
    if (!reference) return undefined;
    const normalized = this.normalizeReference(reference);
    if (!normalized) return undefined;
    return this.resourceIndex.get(normalized) as T | undefined;
  }

  private normalizeReference(reference?: string): string {
    if (!reference) return '';
    const clean = reference.split('?')[0];
    if (clean.startsWith('urn:uuid:') || clean.startsWith('urn:oid:')) {
      return clean;
    }
    const parts = clean.split('/').filter(Boolean);
    if (parts.length >= 2) {
      const type = parts[parts.length - 2];
      const id = parts[parts.length - 1];
      return `${type}/${id}`;
    }
    return clean;
  }

  private filterObservationsByCategory(
    observations: Observation[],
    categoryCode: string,
    patientId?: string,
    encounterId?: string
  ): Observation[] {
    return (observations ?? []).filter(obs => {
      const matchesCategory = (obs.category ?? []).some(cat =>
        (cat.coding ?? []).some(code => (code.code ?? '').toLowerCase() === categoryCode)
      );
      if (!matchesCategory) return false;
      if (obs.status && !['final', 'amended', 'corrected'].includes(obs.status)) {
        return false;
      }
      if (patientId && !this.referenceMatches(obs.subject?.reference, 'Patient', patientId)) {
        return false;
      }
      if (encounterId && obs.encounter?.reference && !this.referenceMatches(obs.encounter.reference, 'Encounter', encounterId)) {
        return false;
      }
      return true;
    });
  }

  private filterPhysicalExamObservations(
    observations: Observation[],
    patientId?: string,
    encounterId?: string
  ): Observation[] {
    return (observations ?? []).filter(obs => {
      if (!this.matchesObservationCategory(obs, 'exam')) {
        return false;
      }
      if (!this.isFinalOrCorrectedObservation(obs)) {
        return false;
      }
      if (this.matchesObservationCategory(obs, 'patient-symptoms')) {
        return false;
      }
      if (patientId && !this.referenceMatches(obs.subject?.reference, 'Patient', patientId)) {
        return false;
      }
      if (encounterId && obs.encounter?.reference && !this.referenceMatches(obs.encounter.reference, 'Encounter', encounterId)) {
        return false;
      }

      return !this.isPatientPerformedObservation(obs, patientId);
    });
  }

  private filterPatientSymptomObservations(
    observations: Observation[],
    patientId?: string,
    encounterId?: string
  ): Observation[] {
    return (observations ?? []).filter(obs => {
      const hasPatientSymptomsMarker = this.matchesObservationCategory(obs, 'patient-symptoms');
      const isLegacyBaseCategory = this.matchesObservationCategory(obs, 'exam') || this.matchesObservationCategory(obs, 'social-history');
      if (!hasPatientSymptomsMarker && !isLegacyBaseCategory) {
        return false;
      }
      if (!this.isFinalOrCorrectedObservation(obs)) {
        return false;
      }
      if (patientId && !this.referenceMatches(obs.subject?.reference, 'Patient', patientId)) {
        return false;
      }
      if (encounterId && obs.encounter?.reference && !this.referenceMatches(obs.encounter.reference, 'Encounter', encounterId)) {
        return false;
      }

      if (hasPatientSymptomsMarker) {
        return true;
      }

      return this.isPatientPerformedObservation(obs, patientId);
    });
  }

  private matchesObservationCategory(observation: Observation, categoryCode: string): boolean {
    return (observation.category ?? []).some(cat =>
      (cat.coding ?? []).some(code => (code.code ?? '').toLowerCase() === categoryCode)
    );
  }

  private isFinalOrCorrectedObservation(observation: Observation): boolean {
    const status = `${observation.status ?? ''}`.toLowerCase();
    return status === 'final' || status === 'corrected';
  }

  private isPatientPerformedObservation(observation: Observation, patientId?: string): boolean {
    const expectedPatientReferences = new Set(
      [
        patientId ? `Patient/${patientId}` : '',
        patientId || ''
      ].filter(Boolean)
    );

    return (observation.performer ?? []).some(performer => {
      const reference = performer.reference ?? '';
      if (!reference) {
        return false;
      }

      const normalized = this.normalizeReference(reference);
      return expectedPatientReferences.has(reference)
        || expectedPatientReferences.has(normalized)
        || normalized.toLowerCase().startsWith('patient/');
    });
  }

  private isLabServiceRequest(sr: ServiceRequest): boolean {
    const categories = (sr.category ?? []) as any[];
    return categories.some(cat => {
      if (this.utility.searchCodeableConceptByDisplayOrText(cat as any, 'laboratory')) return true;
      const codes = (cat?.coding ?? []).map((coding: { code?: string }) => (coding.code ?? '').toLowerCase());
      return codes.includes('laboratory') || codes.includes('lab');
    });
  }

  private isCurrentDiagnosis(condition: Condition, patientId?: string, encounterId?: string): boolean {
    const matchesSubject = !patientId || this.referenceMatches(condition.subject?.reference, 'Patient', patientId);
    const encounterRef = (condition as any).encounter?.reference;
    const matchesEncounter = !!encounterId && !!encounterRef && this.referenceMatches(encounterRef, 'Encounter', encounterId);

    if (!matchesSubject || !matchesEncounter) return false;

    const verification = (condition.verificationStatus?.coding?.[0]?.code || condition.verificationStatus?.text || '').toLowerCase();
    const clinical = (condition.clinicalStatus?.coding?.[0]?.code || condition.clinicalStatus?.text || '').toLowerCase();

    if (['resolved', 'inactive', 'entered-in-error'].includes(clinical)) return false;
    if (['refuted', 'entered-in-error'].includes(verification)) return false;

    return ['provisional', 'differential', 'confirmed'].includes(verification) || clinical === 'active';
  }

  private isHistoricalDiagnosis(condition: Condition, patientId?: string, encounterId?: string): boolean {
    const matchesSubject = !patientId || this.referenceMatches(condition.subject?.reference, 'Patient', patientId);
    if (!matchesSubject) return false;

    if (this.isCurrentDiagnosis(condition, patientId, encounterId)) return false;

    const clinical = (condition.clinicalStatus?.coding?.[0]?.code || condition.clinicalStatus?.text || '').toLowerCase();
    if (clinical === 'entered-in-error') return false;

    return true;
  }

  private referenceMatches(reference: string | undefined, resourceType: string, id: string): boolean {
    if (!reference || !id) return false;
    const normalized = this.normalizeReference(reference);
    return normalized === `${resourceType}/${id}` || normalized.endsWith(`/${id}`) || normalized === id;
  }

  private displayNameForResource(resource?: Resource): string {
    if (!resource) return '';
    switch (resource.resourceType) {
      case 'Practitioner':
        return this.humanName(((resource as Practitioner).name ?? [])[0]);
      case 'PractitionerRole':
        return (resource as PractitionerRole).organization?.display || (resource as PractitionerRole).code?.[0]?.text || '';
      case 'Organization':
        return (resource as Organization).name || '';
      case 'Patient':
        return this.humanName(((resource as Patient).name ?? [])[0]);
      default:
        return (resource as any).name || resource.id || resource.resourceType;
    }
  }

  private humanName(name?: HumanName): string {
    if (!name) return '';
    const parts = [name.given?.join(' '), name.family].filter(Boolean);
    return parts.join(' ').trim();
  }
}
